import { computed, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { getCatalog } from 'src/utils/catalog';
import { origin } from 'src/utils/origin';
import { StorageUtil, TranslationUtils } from 'src/utils/storage.util';
import { UrlUtil } from 'src/utils/url.util';
import { HandleRequest } from './handle-request';
import {
  ApiResponse,
  Configuration,
  ConfigurationParameters,
  ProductCardModel,
  ProductCardModelSpecificationGroupSearchProductResponse,
  ResponseError,
  SearchApi,
  SearchRequestModel
} from './openapi';
import { Translations } from '../common/models';
import { CustomNotificationEvent } from 'src/common/events';


export namespace SearchApiMap {
  export type PaginationModel = {
    link: URL,
    content: string,
    ariaLabel: string,
  }

  export const SIZE = 48;

  let _response = new BehaviorSubject<ProductCardModelSpecificationGroupSearchProductResponse | null>(null);

  let _apiconfig: ConfigurationParameters = { basePath: origin(), };
  let _config = new Configuration(_apiconfig);
  let catalog = getCatalog();
  let group = 'search';
  let _searchApi = new SearchApi(_config);
  let _loading = signal<boolean>(false);
  let _searchHandling: Partial<HandleRequest> = {
    loading: (loading) => _loading.set(loading),
    group,
    timeoutInMs: 5000,
    debounce: 500,
    async onError<T>(context: ResponseError, init: HandleRequest) {
      console.error('context', context?.response)
      let group = (init as HandleRequest).group;
      let id = (init as HandleRequest).id;
      let status = typeof context == 'string' ? context : context?.response?.status;
      let title = TranslationUtils.get(`${group}.${id}.${status}.title`.toUpperCase(),status.toString());
      let text = TranslationUtils.get(`${group}.${id}.${status}.text`.toUpperCase());
      // TODO fix
      CustomNotificationEvent.dispatch({
        title,
        text,
        color: 'var(--error-color, white)',
        backgroundColor: 'var(--error-background-color, red)',
        position: 'top-mid'
      })
      return { value: async () => null, raw: context.response } as ApiResponse<T>;
    },
  }

  export let loading = computed(() => _loading());

  export async function search(searchRequestModel: SearchRequestModel, lastResults: Array<ProductCardModel> = []) {
    let resp = await (await _searchApi.search({ catalog, searchRequestModel }, _searchHandling));
    let clone = structuredClone(resp);
    Object.assign(clone, { results: resp.results ?? [] });
    _response.next(clone);
    return clone;
  }
  export async function searchClean(searchRequestModel: SearchRequestModel) {
    searchRequestModel.from = searchRequestModel.from ?? 0;
    let clone = await search(searchRequestModel, []);
    _response.next(clone);
    return clone;
  }

  export function splitTags(tags?: string | null): string[][] {
    return tags?.split('|').map(t => t.split('&')) ?? [];
  }
  export function joinTags(tags: string[][]): string {
    return tags.map(t => t.join('&')).join('|');
  }

  export function UrlToModel(url: URL, tags:string[][]): SearchRequestModel {
    let size = +(url.searchParams.get(UrlUtil.SIZE_PREFIX) || SIZE);
    // let tags = (url.searchParams.get(UrlUtil.TAGS_PREFIX)?.length ?? 0) > 0 ? splitTags(url.searchParams.get(UrlUtil.TAGS_PREFIX)) : [];
    let from = +(url.searchParams.get(UrlUtil.FROM_PREFIX) || 0);
    let sorting = url.searchParams.get(UrlUtil.SORT_PREFIX) || '';
    let searchTerms = url.searchParams.get(UrlUtil.SEARCH_PREFIX) || '';
    let facets = Facets(Array.from(url.searchParams.entries()));
    let priceRangeInterval = url.searchParams.has(UrlUtil.INTERVAL_PREFIX) ? +(url.searchParams.get(UrlUtil.INTERVAL_PREFIX) || '') : undefined;
    if (priceRangeInterval == 0)
      priceRangeInterval = undefined;
    return { from, size, sorting, tags, searchTerms, facets, priceRangeInterval };
  }

  export function Facets(entries: [key: string, value: string][]): { [key: string]: string[] } {
    let facets: any = {};
    entries.forEach(([key, value]) => {
      if (key.startsWith(UrlUtil.FACET_PREFIX)) {
        const cleanKey = key.replace(UrlUtil.FACET_PREFIX, '');
        if (facets[cleanKey] == null)
          facets[cleanKey] = [];
        facets[cleanKey].push(value)
      }
    });
    return facets;
  }

  export function FormToUrl(form: HTMLFormElement, url: URL): URL {
    Array
      .from(url.searchParams.keys())
      .forEach(key => {
        if (UrlUtil.isReplacableKey(key))
          url.searchParams.delete(key)
      });
    let data = new FormData(form);
    Array.from(
      // @ts-ignore
      new URLSearchParams(data)
        .entries()
    ).forEach(([k, v]) => v && !+v ? url.searchParams.append(k, v) : url.searchParams.delete(k));

    return url;
  }

  export function Pages(result: ProductCardModelSpecificationGroupSearchProductResponse | undefined, url: URL | undefined, shownAmount = 7): PaginationModel[] {
    if (result == null || url == null)
      return [];
    let pages: PaginationModel[] = [];
    let from = Math.floor((result.startingFrom ?? 0) / SIZE);
    let max = Math.ceil((result.totalResults ?? 0) / SIZE);
    let start = Math.max(from - Math.floor(shownAmount / 2), 0);
    let end = Math.min(start + shownAmount, max);

    /**
     * no pages
     */
    if (end <= 0)
      return pages;
    /**
     * make length full not start too early
     */
    if (from + Math.floor(shownAmount / 2) >= max)
      start = Math.max(end - shownAmount, 0);

    if (start > 0) {
      let link = new URL(url);
      link.searchParams.delete('from');
      pages.push({
        content: '<<',
        ariaLabel: 'PAGINATION.TO_START',
        link,
      })
    }
    for (let index = 0; index < shownAmount && index + start < end; index++) {
      let link = new URL(url);
      link.searchParams.set('from', '' + (index + start) * SIZE);
      if (+(link.searchParams.get('from') ?? '') == 0)
        link.searchParams.delete('from');
      pages.push({
        content: '' + (index + start + 1),
        ariaLabel: 'PAGINATION.TO_PAGE',
        link,
      })
    }
    if (end < max) {
      let link = new URL(url);
      link.searchParams.set('from', '' + (max) * SIZE);
      pages.push({
        content: '>>',
        ariaLabel: 'PAGINATION.TO_END',
        link,
      })
    }

    if (pages.length > 1)
      return pages;
    return [];
  }
}
